import { cacheFactory } from '@shared/helpers/function.helpers';
import type { Baseline, Project } from '@shared/models/project.model';
import type { IProposal } from '@shared/models/proposals.model';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import { genericProductsLookup } from '@shared/generic-products';
import type { ProductID, ProductRecord } from '@shared/models/product.model';
import type { Recipe } from '@shared/models/recipe.model';
import { getAllProductElements } from '../recursive-element.helpers';
import { getProductIdsInElement } from '../product.helpers';
import { uniq } from 'lodash';
import { resolveAndEnrichVersionForProposal } from './enrich-resolved-tree.helpers';
import type { ProposalElementRollupContext } from './diff-view.helpers';
import {
  getResolvedBuildingVersionCacheDeps,
  getResolvedVersionCacheProposalKey,
  normalizeResolvedVersionEnrichContext,
  type ResolvedVersionEnrichContext,
} from './resolved-version-enrich-cache.helpers';

export type { ResolvedVersionEnrichContext };
export { buildResolvedVersionEnrichContext } from './resolved-version-enrich-cache.helpers';

/**
 * Lightweight safety-net: walk the enriched element tree and copy any referenced
 * generic products that are missing from {@link Baseline.products}.
 * Category re-enrichment can produce product_id refs (e.g. AluminumProfileNone) that
 * aren't in the version's product catalog yet — the pipeline handles this via
 * {@link getProductIdsFromResolvedProposals}, but this covers optimistic-update windows
 * between pipeline runs without importing the heavier product.helpers module.
 */
const syncMissingGenericProductsInVersion = (version: Baseline): Baseline => {
  const missing: ProductRecord = {};
  for (const el of getAllProductElements(version)) {
    for (const id of [el.product_id, el.generic_product_id]) {
      if (id && !version.products[id] && genericProductsLookup[id]) {
        missing[id] = genericProductsLookup[id]!;
      }
    }
  }
  if (Object.keys(missing).length === 0) return version;
  return { ...version, products: { ...version.products, ...missing } };
};

/**
 * The tree the user sees while a proposal is active — use it wherever the UI or a calculation
 * must reflect the selected proposal rather than the stored baseline.
 *
 * Returns the stored {@link version} when there is no proposal and every referenced
 * generic is already on {@link Baseline.products}. Otherwise returns a version with
 * {@link proposal}'s actions applied and category enrichment re-run. Missing generic
 * products are copied onto that returned object's {@link Baseline.products} only.
 * The stored version is not written. Enrichment makes product slots (product_id,
 * unit, count) match the proposal's property edits — same shape baseline gets from
 * the enrich pipeline. Expensive operation, so cache it via {@link cacheFactory}
 * until `version`, `proposal`, or `version.actions` change.
 *
 * Read side of the parity rule documented on {@link applyProjectChangesWithProposalContext}: a
 * change made with a proposal selected shows up here exactly as the same change made on baseline,
 * apart from the deviations listed there.
 *
 * {@link version.products} is synced via {@link syncMissingGenericProductsInVersion} so
 * every product id on the resolved tree exists in the catalog (pipeline ensures the same
 * on the stored version via {@link getProductIdsFromResolvedProposals}).
 *
 * productElement.id is preserved across enrichment-driven product swaps (slot-by-index reuse).
 */
export const getResolvedBuildingVersion = (
  version: Baseline,
  proposalOrId: ItemOrItemId<IProposal> | undefined,
  enrichContext?: ResolvedVersionEnrichContext | Project,
): Baseline => {
  const normalizedContext =
    normalizeResolvedVersionEnrichContext(enrichContext);
  const proposalKey = getResolvedVersionCacheProposalKey(proposalOrId);
  const projectId = normalizedContext?.project?.id ?? 'no-project';

  return cacheFactory(
    () => {
      const enriched = resolveAndEnrichVersionForProposal(
        version,
        proposalOrId,
        normalizedContext,
      );
      // Baseline mode used to return the stored version as-is. Old elements can
      // still reference a generic that was never copied onto baseline.products.
      return syncMissingGenericProductsInVersion(enriched);
    },
    `resolvedVersion[${version.id}][${proposalKey}][${projectId}]`,
    getResolvedBuildingVersionCacheDeps(
      version,
      proposalOrId,
      normalizedContext,
    ),
  );
};

/**
 * Baseline vs proposal-resolved trees for per-element KPI / diff rollup.
 * The proposal side is {@link getResolvedBuildingVersion} so shares follow the visible tree.
 */
export const getProposalElementRollupContext = (
  version: Baseline,
  proposalOrId: ItemOrItemId<IProposal> | undefined,
  enrichContext?: ResolvedVersionEnrichContext | Project,
): ProposalElementRollupContext => ({
  baselineVersion: version,
  proposalResolvedVersion: getResolvedBuildingVersion(
    version,
    proposalOrId,
    enrichContext,
  ),
});

/**
 * Collect product ids referenced on proposal-resolved element trees (actions applied +
 * category re-enrichment). Used by the enrich pipeline so {@link Baseline.products}
 * contains every id the product list can show when a proposal is active.
 *
 * Uses {@link getResolvedBuildingVersion} so each proposal shares the same cacheFactory
 * entries as the client. Callers should invoke this a single time per pipeline step and
 * pass the resulting {@link Set} down to avoid redundant work.
 */
export const getProductIdsFromResolvedProposals = (
  version: Baseline,
  recipes: Recipe[] = [],
  productsLookup: ProductRecord = {},
  enrichContext?: ResolvedVersionEnrichContext,
): ProductID[] => {
  if (!version.proposals?.length) return [];
  const mergedLookup = { ...productsLookup, ...version.products };
  return uniq(
    version.proposals.flatMap((proposal) =>
      getProductIdsInElement(
        getResolvedBuildingVersion(version, proposal, enrichContext),
        recipes,
        mergedLookup,
      ),
    ),
  );
};
